import React from 'react';
import { formatHours } from '../../utils/formatHours';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Filter,
  DetailRow,
  Toolbar,
  ColumnChooser,
} from '@syncfusion/ej2-react-grids';
import PackageLaborDetail from './PackageLaborDetail';
import tokens from '../../theme/tokens';

/**
 * PackageLaborGrid
 * Syncfusion GridComponent with sort, Excel-like filter, column chooser, and
 * DetailRow inline expansion.
 * Props: rows, mode, weekStart, token
 */

// ── Cell Templates ────────────────────────────────────────────────────────────

const iwpTemplate = (props) => (
  <span style={{ fontFamily: tokens.fontFamily, fontSize: 12, color: '#64748b' }}>
    {props.iwp || '—'}
  </span>
);

const descriptionTemplate = (props) => {
  const displayVal =
    props.description != null && String(props.description).trim() !== ''
      ? props.description
      : props.autodesk_name || '—';
  return <span style={{ fontSize: 12, color: '#1e293b' }}>{displayVal}</span>;
};

const optionalFieldTemplate = (fieldName) => (props) => {
  const v = props[fieldName];
  const text = v != null && String(v).trim() !== '' ? String(v) : '—';
  return <span style={{ fontSize: 12, color: '#64748b' }}>{text}</span>;
};

const fieldPhaseTpl = optionalFieldTemplate('field_phase');
const fieldCostCodeTpl = optionalFieldTemplate('field_cost_code');
const shopPhaseTpl = optionalFieldTemplate('shop_phase');
const shopCostCodeTpl = optionalFieldTemplate('shop_cost_code');
const programAreaTpl = optionalFieldTemplate('program_area');
const workTypeCodeTpl = optionalFieldTemplate('work_type_code');
const workTypeNameTpl = optionalFieldTemplate('work_type_name');
const twpCodeTpl = optionalFieldTemplate('twp_code');

const statusTemplate = (props) => {
  if (!props.tracking_status_label)
    return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: props.tracking_status_color || '#64748b',
        backgroundColor: (props.tracking_status_color || '#64748b') + '18',
        padding: '2px 8px',
        borderRadius: 10,
        border: `1px solid ${props.tracking_status_color || '#e2e8f0'}`,
        whiteSpace: 'nowrap',
      }}
    >
      {props.tracking_status_label}
    </span>
  );
};

const budgetPctTemplate = (props) => {
  if (props.budgetPctCombined === null || props.budgetPctCombined === undefined)
    return <span style={{ fontSize: 12, color: '#94a3b8' }}>No budget</span>;
  const p = props.budgetPctCombined;
  const isOver = p >= 100;
  const isWarn = p >= 85 && !isOver;
  const color = isOver ? '#dc2626' : isWarn ? '#d97706' : '#15803d';
  const barColor = isOver ? '#ef4444' : isWarn ? '#f59e0b' : '#22c55e';
  const bg = isOver ? '#fef2f2' : isWarn ? '#fffbeb' : '#f0fdf4';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          width: 70,
          backgroundColor: '#e2e8f0',
          borderRadius: 4,
          height: 6,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: `${Math.min(p, 100)}%`,
            backgroundColor: barColor,
            height: '100%',
            borderRadius: 4,
          }}
        />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color,
          backgroundColor: bg,
          padding: '2px 7px',
          borderRadius: 10,
          border: `1px solid ${color}22`,
        }}
      >
        {p}%
      </span>
    </div>
  );
};

const earnedPctTemplate = (props) => {
  if (props.earnedPctCombined === null || props.earnedPctCombined === undefined)
    return <span style={{ fontSize: 12, color: '#94a3b8' }}>No budget</span>;
  const color = '#3b82f6';
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color,
        backgroundColor: '#eff6ff',
        padding: '2px 7px',
        borderRadius: 10,
        border: `1px solid ${color}22`,
      }}
    >
      {props.earnedPctCombined}%
    </span>
  );
};

const combinedHrsTemplate = (props) => {
  const actual = props.combinedAlltime || 0;
  const budget = props.combinedBudget;
  const isOver = budget > 0 && actual > budget;
  return (
    <div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: isOver ? '#dc2626' : '#1e293b',
        }}
      >
        {formatHours(actual)}
      </span>
      {budget > 0 && (
        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>
          /{formatHours(budget)}
        </span>
      )}
    </div>
  );
};

const fieldHrsTemplate = (props) => {
  const actual = props.field_hrs_alltime || 0;
  const budget = props.field_hrs_budgeted;
  const isOver = budget && actual > budget;
  return (
    <div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: isOver ? '#dc2626' : '#3b82f6',
        }}
      >
        {formatHours(actual)}
      </span>
      {budget != null && (
        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>
          /{formatHours(budget)}
        </span>
      )}
    </div>
  );
};

const shopHrsTemplate = (props) => {
  const actual = props.shop_hrs_alltime || 0;
  const budget = props.shop_hrs_budgeted;
  const isOver = budget && actual > budget;
  return (
    <div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: isOver ? '#dc2626' : '#8b5cf6',
        }}
      >
        {formatHours(actual)}
      </span>
      {budget != null && (
        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>
          /{formatHours(budget)}
        </span>
      )}
    </div>
  );
};

const weekHrsTemplate = (props) => (
  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
    {formatHours(props.weekHrsTotal || 0)}
  </span>
);

// ── Component ─────────────────────────────────────────────────────────────────

const PackageLaborGrid = ({
  rows,
  mode,
  weekStart,
  token,
  isAdmin,
  projectId,
  detailRefreshKey = 0,
  onCorrectionSuccess,
}) => {
  const rowDataBound = (args) => {
    if (!args.row) return;
    if (args.data.healthStatus === 'over') {
      args.row.style.borderLeft = '3px solid #ef4444';
    } else if (args.data.healthStatus === 'approaching') {
      args.row.style.borderLeft = '3px solid #f59e0b';
    } else {
      args.row.style.borderLeft = '3px solid transparent';
    }
  };

  const detailTemplate = (pkg) => (
    <PackageLaborDetail
      packageId={pkg.package_id}
      packageName={pkg.description || pkg.autodesk_name || 'Unnamed Package'}
      projectId={projectId}
      weekStart={weekStart}
      token={token}
      isAdmin={isAdmin}
      refreshKey={detailRefreshKey}
      onCorrectionSuccess={onCorrectionSuccess}
    />
  );

  const toolbarOptions = ['ColumnChooser'];

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <GridComponent
        key={mode}
        dataSource={rows}
        allowSorting={true}
        allowFiltering={true}
        filterSettings={{ type: 'Excel' }}
        sortSettings={{
          columns: [{ field: 'weekHrsTotal', direction: 'Descending' }],
        }}
        toolbar={toolbarOptions}
        showColumnChooser={true}
        rowDataBound={rowDataBound}
        detailTemplate={mode === 'week' ? detailTemplate : undefined}
        height="auto"
      >
        <ColumnsDirective>
          <ColumnDirective
            field="description"
            headerText="Description"
            width="220"
            template={descriptionTemplate}
            allowSorting={true}
            showInColumnChooser={false}
          />
          <ColumnDirective
            field="autodesk_name"
            headerText="IWP Name"
            width="200"
            allowSorting={true}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="area_name"
            headerText="Area"
            width="120"
            allowSorting={true}
          />
          {mode === 'week' && (
            <ColumnDirective
              field="weekHrsTotal"
              headerText="HRS WEEK"
              width="110"
              template={weekHrsTemplate}
              allowSorting={true}
            />
          )}
          <ColumnDirective
            field="combinedAlltime"
            headerText="HRS TO DATE"
            width="140"
            template={combinedHrsTemplate}
            allowSorting={true}
          />
          <ColumnDirective
            field="budgetPctCombined"
            headerText="% Budget Used"
            width="170"
            template={budgetPctTemplate}
            allowSorting={true}
          />
          <ColumnDirective
            field="earnedPctCombined"
            headerText="% Earned"
            width="110"
            template={earnedPctTemplate}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="tracking_status_label"
            headerText="Status"
            width="130"
            template={statusTemplate}
            allowSorting={true}
          />
          <ColumnDirective
            field="field_hrs_alltime"
            headerText="Field HRS TO DATE"
            width="140"
            template={fieldHrsTemplate}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="shop_hrs_alltime"
            headerText="SHOP HRS TO DATE"
            width="140"
            template={shopHrsTemplate}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="iwp"
            headerText="IWP ID"
            width="110"
            template={iwpTemplate}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="field_phase"
            headerText="Field Phase"
            width="120"
            template={fieldPhaseTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="field_cost_code"
            headerText="Field Cost Code"
            width="130"
            template={fieldCostCodeTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="shop_phase"
            headerText="Shop Phase"
            width="120"
            template={shopPhaseTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="shop_cost_code"
            headerText="Shop Cost Code"
            width="130"
            template={shopCostCodeTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="program_area"
            headerText="Program Area"
            width="140"
            template={programAreaTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="work_type_code"
            headerText="Work Type Code"
            width="130"
            template={workTypeCodeTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="work_type_name"
            headerText="Work Type Name"
            width="160"
            template={workTypeNameTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
          <ColumnDirective
            field="twp_code"
            headerText="TWP Code"
            width="110"
            template={twpCodeTpl}
            allowSorting={true}
            visible={false}
            showInColumnChooser={true}
          />
        </ColumnsDirective>
        <Inject
          services={
            mode === 'week'
              ? [Sort, Filter, DetailRow, Toolbar, ColumnChooser]
              : [Sort, Filter, Toolbar, ColumnChooser]
          }
        />
      </GridComponent>

      {/* Footer hint */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: '1px solid #f1f5f9',
          fontSize: 11,
          color: '#94a3b8',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {mode === 'week'
            ? '💡 Click any column to sort · Toolbar: choose visible columns · Filter icons: Excel-like filters · Click › on any row for employee detail · Budget % is all-time cumulative'
            : '💡 Click any column to sort · Toolbar: choose visible columns · Filter icons: Excel-like filters · Budget % is all-time cumulative'}
        </span>
        <span>{rows.length} packages</span>
      </div>
    </div>
  );
};

export default PackageLaborGrid;
